/**
 * "Continue with Google" for the sign-in/sign-up pages (Google Identity
 * Services). Renders NOTHING unless GOOGLE_CLIENT_ID is configured, so the
 * password flow is untouched until the OAuth client exists. GIS handles the
 * popup and hands back a signed id_token (`credential`); the API verifies it
 * server-side (POST /admins/auth/google) — no OAuth secrets in the client.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/AuthProvider';
import { trackEvent } from '@/utils/analytics';

const GOOGLE_CLIENT_ID = (process.env.GOOGLE_CLIENT_ID as string) || '';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

type GsiCredentialResponse = { credential?: string };
type GoogleAccountsId = {
  initialize: (config: Record<string, unknown>) => void;
  renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
};
type GoogleGlobal = { accounts?: { id?: GoogleAccountsId } };

function loadGsi(): Promise<GoogleAccountsId | null> {
  return new Promise((resolve) => {
    const existing = (window as { google?: GoogleGlobal }).google?.accounts?.id;
    if (existing) return resolve(existing);
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => resolve((window as { google?: GoogleGlobal }).google?.accounts?.id ?? null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}

export default function GoogleSignInButton({
  onError,
  intent = 'sign-in',
}: {
  onError?: (message: string) => void;
  /** Which page hosts the button — gates the `sign_up_started` conversion
      event to the sign-up page's instance (INT-0004). `sign_up` itself fires
      wherever a NEW account is created, since Google creates accounts from
      either page. */
  intent?: 'sign-in' | 'sign-up';
}) {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAdminAuth() as {
    loginWithGoogle: (credential: string) => Promise<{ created?: boolean }>;
  };
  const buttonRef = useRef<HTMLDivElement>(null);
  const [failedToLoad, setFailedToLoad] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadGsi().then((gsi) => {
      if (cancelled || !gsi || !buttonRef.current) {
        if (!gsi) setFailedToLoad(true);
        return;
      }
      gsi.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: GsiCredentialResponse) => {
          if (!response?.credential) return;
          // Conversion funnel (INT-0004): completing the Google popup is the
          // closest observable "started" moment (GIS owns the button click).
          if (intent === 'sign-up') trackEvent('sign_up_started', { method: 'google' });
          try {
            const result = await loginWithGoogle(response.credential);
            if (result?.created) trackEvent('sign_up', { method: 'google' });
            // Brand-new accounts go to org setup; returning admins to the app.
            navigate(result?.created ? '/setup' : '/admin/dashboard', { replace: true });
          } catch (e) {
            onError?.(e instanceof Error ? e.message : 'Google sign-in failed.');
          }
        },
      });
      gsi.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 360,
        text: 'continue_with',
        shape: 'pill',
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!GOOGLE_CLIENT_ID || failedToLoad) return null;

  return (
    <div>
      <div ref={buttonRef} className="flex justify-center" />
      <div className="my-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">or</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
    </div>
  );
}
